'use client';

/**
 * Component Palette
 * Sidebar for adding components to the canvas
 */

import { useState } from 'react';
import {
  ComponentType,
  COMPONENT_TYPE_CONFIG,
} from '@/lib/system-design/types';

interface ComponentPaletteProps {
  onAddComponent: (type: ComponentType) => void;
  canEdit?: boolean;
}

export function ComponentPalette({
  onAddComponent,
  canEdit = true,
}: ComponentPaletteProps) {
  const [searchFilter, setSearchFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'compute' | 'data' | 'network'>('all');

  // Group components by category
  const categories: Record<string, ComponentType[]> = {
    compute: ['service', 'function', 'container'],
    data: ['database', 'cache', 'queue', 'storage'],
    network: ['load-balancer', 'gateway', 'cdn'],
    other: ['client', 'external'],
  };

  // Filter components
  const filterComponents = (types: ComponentType[]) => {
    return types.filter((type) => {
      const config = COMPONENT_TYPE_CONFIG[type];
      if (searchFilter) {
        const search = searchFilter.toLowerCase();
        return (
          config.label.toLowerCase().includes(search) ||
          config.description.toLowerCase().includes(search) ||
          config.technologies.some((t) => t.toLowerCase().includes(search))
        );
      }
      return true;
    });
  };

  const getVisibleCategories = () => {
    if (categoryFilter === 'all') {
      return Object.entries(categories);
    }
    return [[categoryFilter, categories[categoryFilter] || []]];
  };

  return (
    <div className="w-64 bg-background border-r border-border flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h3 className="font-semibold text-foreground mb-3">Components</h3>

        {/* Search */}
        <input
          type="text"
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          placeholder="Search components..."
          className="w-full px-3 py-2 bg-cream/10 border border-border rounded text-foreground text-sm focus:outline-none focus:border-accent mb-3"
        />

        {/* Category tabs */}
        <div className="flex gap-1">
          {(['all', 'compute', 'data', 'network'] as const).map((cat) => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                categoryFilter === cat
                  ? 'bg-accent text-accent-foreground'
                  : 'text-foreground-tertiary hover:bg-cream/10'
              }`}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Component list */}
      <div className="flex-1 overflow-y-auto p-3">
        {getVisibleCategories().map(([category, types]) => {
          const filteredTypes = filterComponents(types as ComponentType[]);
          if (filteredTypes.length === 0) return null;

          return (
            <div key={String(category)} className="mb-4">
              <div className="text-xs font-medium text-foreground-tertiary uppercase tracking-wider mb-2 px-1">
                {String(category)}
              </div>
              <div className="space-y-1">
                {filteredTypes.map((type) => {
                  const config = COMPONENT_TYPE_CONFIG[type];
                  return (
                    <button
                      key={type}
                      onClick={() => canEdit && onAddComponent(type)}
                      disabled={!canEdit}
                      className={`
                        w-full p-2 flex items-center gap-3 rounded-lg transition-colors
                        ${canEdit
                          ? 'hover:bg-cream/10 cursor-pointer'
                          : 'opacity-50 cursor-not-allowed'
                        }
                      `}
                      style={{
                        backgroundColor: canEdit ? undefined : 'transparent',
                      }}
                    >
                      {/* Icon */}
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-lg"
                        style={{ backgroundColor: config.bgColor }}
                      >
                        {config.icon}
                      </div>

                      {/* Info */}
                      <div className="flex-1 text-left">
                        <div className="text-sm font-medium text-foreground">
                          {config.label}
                        </div>
                        <div className="text-xs text-foreground-tertiary truncate">
                          {config.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Help */}
      <div className="p-4 border-t border-border text-xs text-foreground-tertiary">
        <p className="mb-1"><strong>Click</strong> to add component</p>
        <p className="mb-1"><strong>Shift+Drag</strong> from component to create connection</p>
        <p><strong>Scroll</strong> to zoom</p>
      </div>
    </div>
  );
}

export default ComponentPalette;
